import { useEffect, useState } from "react";
import Shell from "@/layout/Shell";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Boxes, LifeBuoy, ShieldCheck } from "lucide-react";
import camApi, { STATUS_META, CONDITION_META } from "@/lib/camApi";
import RaiseTicketDialog from "@/components/cam/RaiseTicketDialog";

export default function MyAssets() {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ticketAsset, setTicketAsset] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const r = await camApi.myAssets(); setAssets(r.assets || []); }
    catch (e) { toast.error(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <Shell role="portal" title="My Assets" subtitle="Equipment assigned to you. Raise a request if anything needs attention.">
      {loading ? (
        <div className="glass rounded-2xl p-12 text-center text-sm text-muted-foreground">Loading your assets…</div>
      ) : assets.length === 0 ? (
        <div className="glass rounded-2xl p-14 text-center max-w-md mx-auto">
          <Boxes className="h-9 w-9 mx-auto text-muted-foreground/40" />
          <h3 className="font-display text-lg tracking-tight mt-3">Nothing assigned yet</h3>
          <p className="text-sm text-muted-foreground mt-1.5">When equipment is assigned to you, it'll show up here with its warranty and condition.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {assets.map((a) => {
            const sm = STATUS_META[a.status] || {};
            const warn = a.warranty_expiry && a.warranty_expiry < new Date().toISOString().slice(0, 10);
            return (
              <div key={a.id} className="glass rounded-2xl p-4 flex flex-col">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-mono text-[11px] text-muted-foreground">{a.asset_tag}</div>
                    <div className="font-display text-base tracking-tight truncate">{a.name}</div>
                  </div>
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold shrink-0">
                    <span className="h-2 w-2 rounded-full" style={{ background: sm.color || "#64748b" }} />{sm.label || a.status}
                  </span>
                </div>
                <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1">
                    <span className="h-1.5 w-1.5 rounded-full" style={{ background: CONDITION_META[a.condition]?.color || "#64748b" }} />
                    <span className="capitalize">{a.condition}</span>
                  </span>
                  {a.warranty_expiry && (
                    <span className={`inline-flex items-center gap-1 ${warn ? "text-red-500" : ""}`}>
                      <ShieldCheck className="h-3.5 w-3.5" />Warranty {new Date(a.warranty_expiry).toLocaleDateString()}
                    </span>
                  )}
                </div>
                <div className="mt-auto pt-3">
                  <Button size="sm" variant="outline" className="w-full" onClick={() => setTicketAsset(a)}>
                    <LifeBuoy className="h-3.5 w-3.5 mr-1.5" />Raise a request
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <RaiseTicketDialog
        key={ticketAsset?.id || "none"}
        asset={ticketAsset}
        open={!!ticketAsset}
        onClose={() => setTicketAsset(null)}
        onRaised={() => { setTicketAsset(null); load(); }}
      />
    </Shell>
  );
}
